'use client';

import { useEffect, useMemo, useRef } from 'react';
import { useDevice } from '@/contexts/DeviceContext';
import { cleanAdCode, parseAdCode as parseGptAdCode, executeAdScripts, initGPT } from '@/lib/gpt-loader';
import AdvertisePopup from '@/components/ads/AdvertisePopup';
import { InArticleAdSlot } from '@/lib/types/articleTemplate';

// GPT "out-of-page"/fluid/ROS-Inread ad formats declare a degenerate size
// like [1,1] — a marker meaning "no fixed visible footprint, sized by the
// creative itself at render time", not a real 1x1-pixel placement. Reserving
// a 1x1 box for these collapses the whole slot down to nothing but its
// floating "Advertise with us" trigger button. Ignore sizes this small when
// picking a placeholder size.
const DEGENERATE_SIZE_THRESHOLD = 5;

/**
 * Parse the largest configured [width, height] out of a GPT defineSlot() call
 * in the raw ad code, so the container can be sized to the ACTUAL ad (a
 * 728x90 leaderboard needs a very different box than a 300x250 MREC) instead
 * of a one-size-fits-all guess. Returns null if no defineSlot() is found
 * (script-only ad code, sized by its own markup) or every declared size is
 * degenerate (ROS-Inread/fluid format) — callers fall back to a sensible
 * default box in that case.
 */
function parseSlotDimensions(adCode: string | null): {
  width: number;
  height: number;
} | null {
  if (!adCode) return null;
  const cleaned = cleanAdCode(adCode);
  if (!cleaned) return null;
  const slotMatch = cleaned.match(/defineSlot\(\s*['"]([^'"]+)['"]\s*,\s*(\[[^\]]+\])\s*,\s*['"]([^'"]+)['"]\s*\)/);
  if (!slotMatch) return null;
  try {
    const sizes = JSON.parse(slotMatch[2]);
    const normalized: number[][] = Array.isArray(sizes[0]) ? sizes : [sizes];
    const realSizes = normalized.filter(([w, h]) => w > DEGENERATE_SIZE_THRESHOLD && h > DEGENERATE_SIZE_THRESHOLD);
    if (realSizes.length === 0) return null;
    // Widest declared size drives the container width; use its paired height.
    return realSizes.reduce((widest, [w, h]) => w > widest.width ? {
      width: w,
      height: h
    } : widest, {
      width: realSizes[0][0],
      height: realSizes[0][1]
    });
  } catch {
    return null;
  }
}

/** Parse the Builder admin's declared "WIDTHxHEIGHT" size label (e.g. "300x250"). */
function parseAdSizeLabel(adSize: string | undefined): {
  width: number;
  height: number;
} | null {
  if (!adSize) return null;
  const match = adSize.match(/^(\d+)\s*x\s*(\d+)$/i);
  if (!match) return null;
  return {
    width: Number(match[1]),
    height: Number(match[2])
  };
}
function generateUniqueCode(): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < 6; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

/**
 * Replace div IDs in ad code with unique IDs to prevent conflicts when the
 * same configured ad slot repeats multiple times on one page.
 */
function makeAdCodeUnique(code: string, uniqueCode: string): string {
  if (!code) return '';
  const foundIds = new Set<string>();
  const gptPattern = /div-gpt-ad-[a-zA-Z0-9_.-]+/g;
  let match;
  while ((match = gptPattern.exec(code)) !== null) {
    foundIds.add(match[0]);
  }
  const idAttrPattern = /id\s*=\s*["']([^"']+)["']/g;
  while ((match = idAttrPattern.exec(code)) !== null) {
    foundIds.add(match[1]);
  }
  if (foundIds.size === 0) return code;
  let result = code;
  const sortedIds = Array.from(foundIds).sort((a, b) => b.length - a.length);
  sortedIds.forEach(originalId => {
    const newId = `${originalId}-${uniqueCode}`;
    result = result.split(originalId).join(newId);
  });
  return result;
}
export interface InArticleAdRendererProps {
  slot: InArticleAdSlot;
  adIndex: number;
}

/**
 * Renders one configured In-Article Ad slot from an article-template's
 * component.props.inArticleAds (NOT the AdSlotConfig store) — shared by every
 * Article*Body template via ArticleTemplateRenderer's buildInlineAdRenderer,
 * so a change here applies to all of them at once.
 *
 * Branches on `slot.contentType` (default 'ad', for slots saved before this
 * field existed):
 * - 'ad': raw GPT ad code. Mirrors ArticleAdSlot's GPT-slot-extraction-with-
 *   script-fallback approach (src/app/api/articles/ArticleListInfinite.tsx)
 *   so raw GPT ad codes render identically to the legacy in-article ad system.
 * - 'image': a plain device-specific image, optionally wrapped in a link.
 * - 'html': raw device-specific custom HTML, with the same script-execution
 *   handling as the 'ad' branch (a custom embed may include a <script>).
 * All three read the desktop/mobile field pair matching their contentType
 * (desktopCode/mobileCode, desktopImage/mobileImage, desktopHtml/mobileHtml).
 */
export default function InArticleAdRenderer({
  slot,
  adIndex
}: InArticleAdRendererProps) {
  const {
    isMobile,
    deviceDetected
  } = useDevice();
  const adContainerRef = useRef<HTMLDivElement>(null);
  const htmlContainerRef = useRef<HTMLDivElement>(null);
  const hasInjectedRef = useRef(false);
  const hasInjectedHtmlRef = useRef(false);
  const slotDivIdRef = useRef<string | null>(null);
  const uniqueCode = useMemo(() => generateUniqueCode(), []);
  const contentType = slot.contentType || 'ad';
  const isAd = contentType === 'ad';
  const adCode = isAd && deviceDetected ? (isMobile ? slot.mobileCode || slot.desktopCode : slot.desktopCode) || null : null;
  const html = contentType === 'html' && deviceDetected ? (isMobile ? slot.mobileHtml || slot.desktopHtml : slot.desktopHtml) || slot.html || null : null;
  const image = contentType === 'image' && deviceDetected ? (isMobile ? slot.mobileImage || slot.desktopImage : slot.desktopImage) || slot.imageLinkUrl || null : null;

  // 'html' content: same script-execution approach as the 'ad' branch below
  // (custom HTML can embed a <script>-based widget, e.g. a third-party embed),
  // just without any of the GPT-specific slot/dimension machinery.
  useEffect(() => {
    if (!html || !htmlContainerRef.current || hasInjectedHtmlRef.current) return;
    hasInjectedHtmlRef.current = true;
    const cleaned = cleanAdCode(html);
    if (!cleaned) return;
    const {
      html: parsedHtml,
      scripts
    } = parseGptAdCode(cleaned);
    htmlContainerRef.current.innerHTML = parsedHtml;
    if (scripts.length > 0) executeAdScripts(scripts);
  }, [html]);
  // "in-read" is the Builder admin's explicit label for a fluid/self-sizing
  // GPT format (matches the legacy ArticleAdSlot's isRosInread special case)
  // — trust that label over inferring size from the raw ad code, which is
  // exactly what led to the earlier 1x1-collapse bug.
  const isInRead = slot.adSize === 'in-read';
  const dimensions = useMemo(() => parseAdSizeLabel(slot.adSize) ?? parseSlotDimensions(adCode), [slot.adSize, adCode]);
  useEffect(() => {
    if (!adCode || !adContainerRef.current || hasInjectedRef.current) return;
    hasInjectedRef.current = true;
    const container = adContainerRef.current;
    const cleaned_0 = cleanAdCode(adCode);
    if (!cleaned_0) return;
    const slotMatch = cleaned_0.match(/defineSlot\(\s*['"]([^'"]+)['"]\s*,\s*(\[[^\]]+\])\s*,\s*['"]([^'"]+)['"]\s*\)/);
    if (!slotMatch) {
      const uniqueCode2 = makeAdCodeUnique(cleaned_0, `${uniqueCode}_${adIndex}`);
      const {
        html: html_0,
        scripts: scripts_0
      } = parseGptAdCode(uniqueCode2);
      container.innerHTML = `<div style="display:flex;justify-content:center;align-items:center;text-align:center;width:100%;">${html_0}</div>`;
      if (scripts_0.length > 0) {
        executeAdScripts(scripts_0);
      }
      return;
    }
    const [, slotPath, sizesStr, originalDivId] = slotMatch;
    let sizes: any;
    try {
      sizes = JSON.parse(sizesStr);
    } catch {
      sizes = [300, 250];
    }

    // Skip for in-read — it uses its own per-device sizes from the ad code
    // (desktop/mobile codes already differ upstream), matching the legacy
    // ArticleAdSlot's isRosInread skip.
    if (!isInRead && typeof window !== 'undefined' && window.innerWidth < 768) {
      const normalizedSizes: number[][] = Array.isArray(sizes[0]) ? sizes : [sizes];
      const hasSmallSize = normalizedSizes.some((s: number[]) => s[0] <= 336);
      if (!hasSmallSize) {
        sizes = [...normalizedSizes, [300, 250], [336, 280]];
      }
    }
    const uniqueDivId = `${originalDivId}-${uniqueCode}-${adIndex}`;
    slotDivIdRef.current = uniqueDivId;
    container.innerHTML = `<div id="${uniqueDivId}" style="width:100%;min-height:${Array.isArray(sizes[0]) ? sizes[0][1] : sizes[1] || 250}px;overflow:hidden;display:flex;justify-content:center;align-items:center;text-align:center;"></div>`;
    initGPT(() => {
      const googletag = (window as any).googletag;
      if (!googletag) return;
      googletag.cmd.push(() => {
        try {
          const gptSlot = googletag.defineSlot(slotPath, sizes, uniqueDivId);
          if (gptSlot) {
            gptSlot.addService(googletag.pubads());
            googletag.enableServices();
            googletag.display(uniqueDivId);
            googletag.pubads().refresh([gptSlot]);
          }
        } catch (err) {
          console.error('[InArticleAdRenderer] Error in cmd.push:', err);
        }
      });
    });
  }, [adCode, uniqueCode, adIndex]);
  useEffect(() => {
    return () => {
      const divId = slotDivIdRef.current;
      try {
        const googletag_0 = (window as any).googletag;
        if (googletag_0?.pubads && divId) {
          const allSlots = googletag_0.pubads()?.getSlots?.() || [];
          const slotsToDestroy = allSlots.filter((s_0: any) => s_0.getSlotElementId() === divId);
          if (slotsToDestroy.length > 0) {
            googletag_0.destroySlots(slotsToDestroy);
          }
        }
      } catch {/* ignore */}
      if (adContainerRef.current) {
        adContainerRef.current.innerHTML = '';
      }
      hasInjectedRef.current = false;
      slotDivIdRef.current = null;
    };
  }, []);
  if (contentType === 'image') {
    if (!image) return null;
    const img =
    // eslint-disable-next-line @next/next/no-img-element
    <img src={image} alt="" className="w-full h-auto" />;
    // imageLinkUrl doubles as the fallback image source (see InArticleAdSlot's
    // doc comment) — only treat it as a click-through when a dedicated
    // desktopImage/mobileImage src is also set, so the image doesn't link to
    // itself when it's the only field the admin filled in.
    const clickThroughUrl = slot.desktopImage || slot.mobileImage ? slot.imageLinkUrl : undefined;
    return <div className="w-full my-4 flex justify-center">
        <div className="relative" style={{
        maxWidth: '336px',
        width: '100%'
      }}>
          {clickThroughUrl ? <a href={clickThroughUrl} target="_blank" rel="noopener noreferrer">{img}</a> : img}
        </div>
      </div>;
  }
  if (contentType === 'html') {
    if (!html) return null;
    return <div className="w-full my-4 flex justify-center">
        <div ref={htmlContainerRef} style={{
        width: '100%'
      }} />
      </div>;
  }
  if (!adCode) return null;

  // in-read: fluid/self-sizing format — render as-is with no fixed box, no
  // gray background, no "Advertise with us" chrome, matching the legacy
  // ArticleAdSlot's isRosInread branch exactly (a boxed placeholder would
  // either clip or misrepresent an ad that sizes itself at render time).
  if (isInRead) {
    return <div className="w-full my-4 flex justify-center">
        <div ref={adContainerRef} style={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center' as const
      }} />
      </div>;
  }

  // Size the wrapper to the ad's actual configured width (a 728x90 leaderboard
  // needs a very different box than a 300x250 MREC — a fixed 336px guess
  // either clips wide ads or leaves narrow ones swimming in whitespace), and
  // reserve the real height up front so the ad doesn't shift content once
  // GPT initializes it a moment later.
  const maxWidth = dimensions ? `${dimensions.width}px` : '336px';
  const minHeight = dimensions ? dimensions.height : 250;
  return <div className="w-full my-4 flex justify-center">
      <div className="bg-gray-100 p-[5px] relative overflow-hidden" style={{
      maxWidth,
      width: '100%'
    }}>
        <div ref={adContainerRef} style={{
        width: '100%',
        minHeight,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center' as const,
        overflow: 'hidden'
      }} />
        <AdvertisePopup />
      </div>
    </div>;
}